import moment, {Moment} from 'moment';

/** Only the shape the default window is derived from. */
type ProjectStart = {
	projectStartYear?: number | null;
	projectStartMonth?: number | null;
	projectStartDay?: number | null;
};

type WorkDetailWindowConfig = {
	workDetailStartDate?: Moment | null;
	workDetailEndDate?: Moment | null;
	workDetailWindowTouched?: boolean | null;
};

/**
 * The earliest start date among the selected projects, used as the lower bound of the default
 * work-detail window — "everything done to date". Falls back to a year ago when no selected project
 * reports a start date, so the picker always has a concrete date to show.
 */
export const earliestProjectStartDate = (selectedProjects: ProjectStart[] | null | undefined): Moment => {
	const starts = (selectedProjects || []).flatMap(p =>
		p.projectStartYear && p.projectStartMonth && p.projectStartDay
			? [moment({year: p.projectStartYear, month: p.projectStartMonth - 1, day: p.projectStartDay})]
			: []
	);
	return starts.length ? moment.min(starts) : moment().subtract(1, 'year');
};

/**
 * The work-detail window a config should carry after the project selection changed.
 *
 * Returns the operator's own window untouched once they have picked one, and otherwise recomputes the
 * default — the earliest selected project's start through today.
 *
 * Recomputing rather than keeping the first value matters because the selection is what the default is
 * derived from, and it is empty on the first pass whenever the modal opens from the invoicing page
 * instead of from a project. Keeping that first value would pin every such invoice's window to
 * earliestProjectStartDate's no-project fallback of a year ago, and silently drop older work from the
 * snapshot and from its printed totals.
 *
 * `workDetailWindowTouched` is the only thing that separates a seeded default from a deliberate
 * choice: by the time the picker is on screen the dates are always set, so their truthiness says
 * nothing about who set them.
 */
export const seedWorkDetailWindow = (
	config: WorkDetailWindowConfig,
	selectedProjects: ProjectStart[] | null | undefined
): {startDate: Moment; endDate: Moment} => {
	const seeded = {startDate: earliestProjectStartDate(selectedProjects), endDate: moment()};
	if (!config.workDetailWindowTouched) {
		return seeded;
	}
	// The picker is not clearable, so a touched window always has both dates; the fallbacks only keep
	// the window a concrete range for anything that reads it.
	return {
		startDate: config.workDetailStartDate ?? seeded.startDate,
		endDate: config.workDetailEndDate ?? seeded.endDate,
	};
};
